export type Language = 'en' | 'de' | 'es' | 'fr' | 'pt' | 'ru' | 'it' | 'sv' | 'zh';

export interface LanguageConfig {
  code: Language;
  name: string;
  nativeName: string;
  flag: string;
}

export const languages: LanguageConfig[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
];


export const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.shop': 'Shop',
    'nav.reviews': 'Reviews',
    'nav.howItWorks': 'How It Works',
    'nav.howTo': 'Guide',
    'nav.about': 'About',
    'nav.contact': 'Contact',
    'nav.blog': 'Blog',
    'nav.cart': 'Cart',
    'nav.account': 'Account',
    'nav.logout': 'Log Out',
    'nav.login': 'Log In',
    
    // Hero Section
    'hero.title': 'Premium Novelty ID Cards',
    'hero.subtitle': 'Create stunning custom ID cards with our premium templates. High-quality designs, fast worldwide delivery.',
    'hero.cta.primary': 'Create Your ID',
    'hero.cta.secondary': 'See Examples',
    
    // Trust Indicators
    'trust.reviews': 'Reviews',
    'trust.countries': 'Countries',
    'trust.cards': 'Cards Created',
    'trust.support': '24/7 Support',
    
    // Products
    'products.title': 'ID Cards',
    'products.subtitle': 'Choose from our collection of premium ID card designs',
    'products.viewAll': 'View All Templates',
    'products.addToCart': 'Add to Cart',
'products.customize': 'Customize Now',
    'products.shopNow': 'Shop Now',
    'products.from': 'From',
    'products.bestseller': 'Bestseller',
    'products.new': 'New',
    'products.sale': 'Sale',
    
    // How It Works
    'howItWorks.title': 'How It Works',
    'howItWorks.subtitle': 'Get your custom ID card in 3 easy steps',
    'howItWorks.step1.title': 'Choose Template',
    'howItWorks.step1.desc': 'Browse our collection and select your preferred design',
    'howItWorks.step2.title': 'Customize',
    'howItWorks.step2.desc': 'Upload your photo and enter your details',
    'howItWorks.step3.title': 'Receive',
    'howItWorks.step3.desc': 'Fast, discreet delivery to your doorstep',
    
    // Testimonials
    'testimonials.title': 'What Our Customers Say',
    'testimonials.subtitle': 'Trusted by thousands worldwide',
    
    // Footer
    'footer.company': 'Company',
    'footer.support': 'Support',
    'footer.legal': 'Legal',
    'footer.newsletter': 'Subscribe to our newsletter',
    'footer.newsletterPlaceholder': 'Enter your email',
    'footer.subscribe': 'Subscribe',
    'footer.rights': 'All rights reserved',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.refund': 'Refund Policy',
    'footer.withdrawal': 'Withdrawal Policy',
    'footer.cookies': 'Cookie Policy',
    'footer.imprint': 'Legal Notice',
    'footer.faq': 'FAQ',
    
    // Cart
    'cart.title': 'Your Cart',
    'cart.empty': 'Your cart is empty',
    'cart.subtotal': 'Subtotal',
    'cart.checkout': 'Checkout',
    'cart.continueShopping': 'Continue Shopping',
    'cart.remove': 'Remove',
    'cart.quantity': 'Quantity',
    'cart.total': 'Total',
    'cart.tax': 'Tax included',
    'cart.couponPlaceholder': 'Discount code',
    'cart.emptySubtext': 'Add products to get started',
    'cart.yourCardDetails': 'Your Card Details',
    'cart.hideDetails': 'Hide Details',
    
    // Auth
    'auth.login': 'Log In',
    'auth.signup': 'Sign Up',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.forgotPassword': 'Forgot password?',
    'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account?',
    'auth.loginSuccess': 'Successfully logged in',
    'auth.logoutSuccess': 'Successfully logged out',
    'auth.signupSuccess': 'Account created successfully',
    
    // Checkout
    'checkout.title': 'Checkout',
    'checkout.shipping': 'Shipping',
    'checkout.payment': 'Payment',
    'checkout.summary': 'Order Summary',
    'checkout.placeOrder': 'Place Order',
    'checkout.processing': 'Processing...',
    'checkout.validatingCoupon': 'Validating coupon...',
    'checkout.success': 'Order Placed Successfully!',
    'checkout.successMessage': 'Thank you for your order. You will receive a confirmation email shortly.',
    'checkout.backToShop': 'Back to Shop',
    'checkout.viewOrder': 'View Order',
    'checkout.paymentCancelled': 'Payment was cancelled. Your cart is still saved – you can try again anytime.',
    'checkout.redirectingToPayment': 'Redirecting to payment...',
    'checkout.redirect.title': 'Redirecting to payment...',
    'checkout.redirect.transmitting': 'Transmitting order data...',
    'checkout.redirect.preparing': 'Preparing payment page...',
    'checkout.redirect.almostDone': 'Almost done — please wait...',
    'checkout.redirect.patience': 'This may take up to 20 seconds.',
    
    // Product Page
    'product.addToCart': 'Add to Cart',
    'product.customize': 'Customize Your Card',
    'product.features': 'Features',
    'product.specifications': 'Specifications',
    'product.reviews': 'Reviews',
    'product.relatedProducts': 'Related Products',
    'product.inStock': 'In Stock',
    'product.outOfStock': 'Out of Stock',
    'product.emojiNotSupported': 'Emojis are not supported in this field.',
    'product.heading': 'Configure <accent>ID Card</accent>',
    'product.photoUpload': 'Upload Photo',
    'product.photoChange': 'Change Photo',
    'product.flipToBack': 'Back Side',
    'product.flipToFront': 'Front Side',
    'product.allFields': 'All Fields',
    'product.previewHint': 'Click fields to enter your details • Flip card for back side',
    'product.previewHintEdit': 'Click on fields to edit • Changes update in real-time',
    'product.signature': 'Signature',
    'product.editTitle': 'Edit',
    
    // Account
    'account.title': 'My Account',
    'account.orders': 'My Orders',
    'account.settings': 'Settings',
    'account.noOrders': 'No orders yet',
    'account.orderHistory': 'Order History',
    'account.viewDetails': 'View Details',
    
    // Buttons
    'button.submit': 'Submit',
    'button.cancel': 'Cancel',
    'button.save': 'Save',
    'button.close': 'Close',
    'button.back': 'Back',
    'button.next': 'Next',
    'button.learnMore': 'Learn More',
    'button.getStarted': 'Get Started',
    'button.viewAll': 'View All',
    'button.readMore': 'Read More',
    
    // General
    'general.loading': 'Loading...',
    'general.error': 'Something went wrong',
    'general.tryAgain': 'Try Again',
    'general.search': 'Search',
    'general.filter': 'Filter',
    'general.sort': 'Sort',
    'general.all': 'All',
    'general.yes': 'Yes',
    'general.no': 'No',
    'general.save': 'Save',
    'general.cancel': 'Cancel',
    
    // Account Page Extended
    'account.profile': 'Profile Data',
    'account.profileDesc': 'Your contact information',
    'account.edit': 'Edit',
    'account.editProfile': 'Edit Profile',
    'account.editProfileDesc': 'Update your contact data and shipping address.',
    'account.name': 'Name',
    'account.namePlaceholder': 'Your full name',
    'account.phone': 'Phone',
    'account.email': 'Email',
    'account.shippingAddress': 'Shipping Address',
    'account.street': 'Street & Number',
    'account.postalCode': 'Postal Code',
    'account.city': 'City',
    'account.country': 'Country',
    'account.saving': 'Saving...',
    'account.saveSuccess': 'Profile updated successfully',
    'account.saveError': 'Error saving profile',
    'account.noProfile': 'Click "Edit" to add your profile data.',
    'account.changeEmail': 'Change Email',
    'account.changeEmailDesc': 'Enter your new email address. You will receive a confirmation email.',
    'account.currentEmail': 'Current Email',
    'account.newEmail': 'New Email Address',
    'account.changing': 'Changing...',
    'account.emailChangeInitiated': 'Email change initiated. Please check your inbox.',
    'account.changePassword': 'Change Password',
    'account.changePasswordDesc': 'Enter your new password. It must be at least 6 characters.',
    'account.password': 'Password',
    'account.changePasswordLabel': 'Change your password',
    'account.newPassword': 'New Password',
    'account.confirmPassword': 'Confirm Password',
    'account.minChars': 'At least 6 characters',
    'account.repeatPassword': 'Repeat password',
    'account.passwordChanged': 'Password changed successfully',
    'account.ordersTitle': 'Orders',
    'account.ordersCount': '{count} order(s)',
    'account.noOrdersYet': 'No orders yet',
    'account.noOrdersMessage': 'You haven\'t placed any orders yet.',
    'account.shopNow': 'Shop Now',
    'account.totalOrders': 'Total Orders',
    'account.totalSpent': 'Total Spent',
    'account.welcomeBack': 'Welcome back, {name}!',
    
    // Order Status
    'order.status.new': 'New',
    'order.status.printing': 'In Production',
    'order.status.shipped': 'Shipped',
    'order.status.done': 'Completed',
    'order.status.cancelled': 'Cancelled',
    'orderDetail.backToAccount': 'Back to My Account',
    'orderDetail.order': 'Order',
    'orderDetail.updatedAt': 'Last updated:',
    'orderDetail.progress': 'Order Progress',
    'orderDetail.tracking': 'Shipment Tracking',
    'orderDetail.trackingCarrier': 'Shipping carrier',
    'orderDetail.trackingNumber': 'Tracking number:',
    'orderDetail.track': 'Track',
    'orderDetail.estimatedDelivery': 'Estimated delivery:',
    'orderDetail.trackingHistory': 'Tracking history',
    'orderDetail.trackingPending': 'Tracking will be available once your order has been shipped.',
    'orderDetail.orderedCards': 'Ordered Cards',
    'orderDetail.orderedCard': 'Ordered Card',
    'orderDetail.card': 'Card',
    'orderDetail.signature': 'Signature',
    'orderDetail.shippingAddress': 'Shipping Address',
    'orderDetail.noAddress': 'No shipping address on file',
    'orderDetail.documents': 'Documents',
    'orderDetail.documentsDesc': 'Invoices and credit notes for your order',
    'orderDetail.invoice': 'Invoice',
    'orderDetail.creditNote': 'Credit Note',
    'orderDetail.cancellationInvoice': 'Cancellation Invoice',
    'orderDetail.pdfError': 'Could not generate PDF',
    'orderDetail.helpTitle': 'Questions about your order?',
    'orderDetail.helpDesc': 'Our support team is happy to help.',
    'orderDetail.contactUs': 'Contact Us',
    'orderDetail.notFound': 'Order not found',
    'orderDetail.loadError': 'Error loading order',
    'orderDetail.copied': 'Tracking number copied',
    'orderDetail.payment.pending': 'Pending',
    'orderDetail.payment.paid': 'Paid',
    'orderDetail.payment.failed': 'Failed',
    'orderDetail.payment.refunded': 'Refunded',
    
    // Checkout Page Extended
    'checkout.contactShipping': 'Contact & Shipping Details',
    'checkout.firstName': 'First Name',
    'checkout.lastName': 'Last Name',
    'checkout.street': 'Street & House Number',
    'checkout.streetName': 'Street',
    'checkout.streetPlaceholder': 'Main Street',
    'checkout.houseNumber': 'House No.',
    'checkout.addressExtra': 'Address Line 2 (optional)',
    'checkout.addressExtraPlaceholder': 'Apartment, suite, etc.',
    'checkout.zip': 'ZIP Code',
    'checkout.city': 'City',
    'checkout.country': 'Country',
    'checkout.selectCountry': 'Select country',
    'checkout.deliveryDE': 'Est. delivery: 3–5 business days (estimate)',
    'checkout.deliveryEU': 'Est. delivery: 7–10 business days (estimate)',
    'checkout.deliveryIntl': 'Est. delivery: 14+ business days (estimate)',
    'checkout.maintenanceMessage': 'We are currently performing maintenance. Please try again later.',
    'checkout.loadingData': 'Loading your data...',
    'checkout.mailboxHint': 'Please ensure your name is visible on the mailbox. We ship via letter post – some PO boxes or parcel stations may not be able to receive letters.',
    'checkout.phoneOptional': 'Phone (optional)',
    'checkout.businessCustomer': 'I am a business customer (B2B)',
    'checkout.companyName': 'Company Name',
    'checkout.vatId': 'VAT ID',
    'checkout.vatIdHint': 'EU VAT identification number (e.g. DE123456789)',
    'checkout.reverseChargeInfo': 'As an EU business customer with a valid VAT ID, the reverse charge procedure applies (0% VAT).',
    'checkout.vatIdVerified': 'VAT ID verified',
    'checkout.vatIdFormatInvalid': 'Invalid VAT ID format. Please check the country prefix and number.',
    'checkout.vatIdNotFound': 'This VAT ID was not found in the EU VIES database.',
    'checkout.viesUnavailable': 'The EU validation service is temporarily unavailable. Please try again.',
    'checkout.companyNameRequired': 'Company name is required for B2B orders.',
    'checkout.vatIdRequired': 'VAT ID is required for B2B orders.',
    'checkout.vatIdInvalidBlock': 'Please enter a valid EU VAT ID before placing your order.',
    'checkout.vatIdStillValidating': 'Please wait – VAT ID is being validated.',
    'checkout.legalConsents': 'Legal Consents',
    'checkout.acceptTerms': 'I accept the',
    'checkout.and': 'and',
    'checkout.termsLink': 'Terms & Conditions',
    'checkout.privacyLink': 'Privacy Policy',
    'checkout.withdrawalNotice': 'I acknowledge that my',
    'checkout.withdrawalLink': 'right of withdrawal',
    'checkout.withdrawalText': 'expires for custom products once production has begun (§ 312g Abs. 2 Nr. 1 BGB).',
    'checkout.requiredFields': '* Required fields',
    'checkout.quantity': 'Quantity',
    'checkout.shippingFree': 'Free',
    'checkout.total': 'Total',
    'checkout.payNow': 'Pay Now',
    'checkout.securePayment': 'Secure Payment',
    
    // Form Validation
    'validation.required': 'required',
    'validation.firstNameRequired': 'First name is required',
    'validation.lastNameRequired': 'Last name is required',
    'validation.emailRequired': 'Email is required',
    'validation.emailInvalid': 'Invalid email address',
    'validation.streetRequired': 'Street & number required',
    'validation.houseNumberRequired': 'House number is required',
    'validation.zipRequired': 'ZIP code is required',
    'validation.cityRequired': 'City is required',
    'validation.stateRequired': 'State/Province is required',
    'validation.phoneInvalid': 'Invalid format. Example: {example}',
    'validation.acceptTerms': 'Please accept the terms',
    'validation.acceptWithdrawal': 'Please acknowledge',
    'validation.fixFields': 'Please fill in the following fields:',
    'validation.newEmailRequired': 'Please enter a new email address',
    'validation.emailMustDiffer': 'New email must be different from current',
    'validation.passwordRequired': 'Please enter a new password',
    'validation.passwordMinLength': 'Password must be at least 6 characters',
    'validation.passwordMismatch': 'Passwords do not match',

    // Checkout Complete
    'checkoutComplete.title': 'Order Confirmed!',
    'checkoutComplete.subtitle': 'Thank you for your order. Your order has been confirmed and is being processed.',
    'checkoutComplete.emailTitle': 'Email Confirmation',
    'checkoutComplete.emailDesc': 'You will receive a confirmation email with your order details shortly.',
    'checkoutComplete.accountTitle': 'Your Account',
    'checkoutComplete.accountDesc': 'We\'ve sent you an email with a link to access your personal account — track your order, view history, and download invoices.',
    'checkoutComplete.shippingTitle': 'Shipping',
    'checkoutComplete.shippingDesc': 'Your order will be processed and shipped within 3–14 business days depending on your location.',
    'checkoutComplete.shippingEstimate': 'Estimate – actual delivery time may vary.',
    'checkoutComplete.tracking': 'Tracking',
    'checkoutComplete.trackingCopied': 'Tracking number copied!',
    'checkoutComplete.trackWithDHL': 'Track with DHL',
    'checkoutComplete.continueShopping': 'Continue Shopping',
    'checkoutComplete.viewAccount': 'View My Account',

    // Checkout Cancelled
    'checkoutCancelled.title': 'Payment Cancelled',
    'checkoutCancelled.subtitle': 'Payment was cancelled. Don\'t worry – nothing was charged.',
    'checkoutCancelled.whatHappened': 'What happened?',
    'checkoutCancelled.explanation': 'The payment was either manually cancelled or there was an issue with the payment method. You can try again anytime.',
    'checkoutCancelled.contact': 'Contact',
    'checkoutCancelled.backToShop': 'Back to Shop',

    // 404 Not Found
    'notFound.title': 'Page not found',
    'notFound.backHome': 'Return to Home',

    // Magic Link
    'magicLink.checkEmail': 'Check Your Email',
    'magicLink.sentTo': 'We sent a sign-in link to',
    'magicLink.clickLink': 'Click the link in your email to log in instantly. The link expires in 1 hour.',
    'magicLink.sent': 'Magic Link sent!',
    'magicLink.sentDesc': 'Check your inbox and click the link to sign in.',
    'magicLink.noAccount': 'No account found with this email address.',
    'magicLink.signupSuccess': 'Registration successful!',
    'magicLink.signupSuccessDesc': 'We\'ve sent you a confirmation email. Please check your inbox and click the link to activate your account.',
  },
} as Partial<Record<Language, Record<string, string>>> as Record<Language, Record<string, string>>;

// Dynamic translation loader — loads non-English translations on demand
const translationCache = new Map<Language, Record<string, string>>();

export async function loadTranslations(lang: Language): Promise<Record<string, string>> {
  if (lang === 'en') return translations.en;
  if (translationCache.has(lang)) return translationCache.get(lang)!;

  let strings: Record<string, string>;
  switch (lang) {
    case 'de': strings = (await import('./translations-de')).default; break;
    case 'es': strings = (await import('./translations-es')).default; break;
    case 'fr': strings = (await import('./translations-fr')).default; break;
    case 'pt': strings = (await import('./translations-pt')).default; break;
    case 'ru': strings = (await import('./translations-ru')).default; break;
    case 'it': strings = (await import('./translations-it')).default; break;
    case 'sv': strings = (await import('./translations-sv')).default; break;
    case 'zh': strings = (await import('./translations-zh')).default; break;
    default: strings = translations.en;
  }

  translationCache.set(lang, strings);
  return strings;
}
